import React, { useCallback, useMemo, memo } from "react"
import Dialog from "@material-ui/core/Dialog"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import MuiDialogContent from "@material-ui/core/DialogContent"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import { withStyles, makeStyles } from "@material-ui/core/styles"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import globalUseStyles from "@/globalUseStyles"
import { updateSingleProjectByProjectIdRequest } from "@/redux/actions/projectAction"
import { useSelector, useDispatch } from "react-redux"
import { getProductLink } from "@/utils"

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(5),
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
        width: "28px",
        height: "28px",
        "&:hover": {
            backgroundColor: theme.palette.primary.secondary,
        },
    },
})

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    )
})

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(0),
    },
}))(MuiDialogContent)

const useStyles = makeStyles(() => ({
    dialogContent: {
        zIndex: "1",
    },
}))

const ModalComplete = memo((props) => {
    const { modalCompleteProjectShow, handleClose } = props
    const classes = useStyles()
    const globalClasses = globalUseStyles()
    const dispatch = useDispatch()
    const { projects } = useSelector((state) => state.projectReducer)
    const { participants } = useSelector((state) => state.participantReducer)
    const { haveRole, noRole } = participants

    const handleComplete = useCallback(() => {
        const { projectId, productId } = projects[0]
        const newProject = {
            projectLaunchDate: Date.now(),
        }
        const productLink = getProductLink(productId)
        dispatch(updateSingleProjectByProjectIdRequest({ projectId, newProject, redirect: productLink ?? false }))
        // finish status page with a counter
    }, [projects])

    const canSubmit = useMemo(() => {
        // can pass complete project validate:
        // 1: all participant finish assessments
        // 2: minium three for each role
        if (noRole.length === 0 && haveRole.length > 0) {
            const participantList = haveRole
            const finishAll =
                participantList.filter((p) => p.questionCount > 0 && p.responseCount < p.questionCount).length === 0 &&
                !participantList.filter((participant) => participant.role_id === null && participant.active !== 1).length
            const participantGroupByRoleId =
                participantList &&
                participantList.reduce((r, a) => {
                    r[`role${a.roleId}`] = r[`role${a.roleId}`] || []
                    r[`role${a.roleId}`].push(a)
                    return r
                }, Object.create(null))
            let countStatus = true
            if (participantGroupByRoleId) {
                const rolesGroupInvalid = Object.keys(participantGroupByRoleId)
                    .filter((key) => key !== "role0")
                    .find((key) => participantGroupByRoleId[key].length < 3)
                countStatus = !rolesGroupInvalid
            }
            return finishAll && countStatus
        }
        return false
    }, [noRole, haveRole])

    // check finish condition and output different
    const outputContentSubmit = useMemo(() => {
        return (
            <Box textAlign="center">
                <Box mt={6} mb={5}>
                    <Typography variant="h5" component="h2">
                        Complete Project?
                    </Typography>
                </Box>
                <Typography className={globalClasses.paragraph}>Thank you for completing the project.</Typography>
                <Typography className={globalClasses.paragraph}>Please click confirm to continue. This action cannot be undone.</Typography>
                <Box mt={5} mb={6} display="flex" alignItems="center" justifyContent="center">
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleComplete} style={{ marginLeft: "10px" }}>
                        Confirm
                    </Button>
                </Box>
            </Box>
        )
    }, [])
    const outputCannotSubmit = useMemo(() => {
        return (
            <Box textAlign="center">
                <Box mt={6} mb={5}>
                    <Typography variant="h5" component="h2">
                        Cannot Submit
                    </Typography>
                </Box>
                <Typography className={globalClasses.paragraph}>Thank you for completing the project.</Typography>
                <Typography className={globalClasses.paragraph}>Please click confirm to continue. This action cannot be undone.</Typography>
                <Box display="flex" textAlign="left" justifyContent="center">
                    <ol>
                        <li>
                            <Typography>ADD more participants to meet the minimum requirement</Typography>
                        </li>
                        <li>
                            <Typography>Wait for participants to finish</Typography>
                        </li>
                        <li>
                            <Typography>REMIND the outstanding participants by sending them a message</Typography>
                        </li>
                        <li>
                            <Typography className={globalClasses.paragraph}>
                                DELETE the participants that are not responsive to finish
                            </Typography>
                        </li>
                    </ol>
                </Box>
                <Typography>Remember the more participants that respond, the more accurate the result</Typography>
                <Box mt={5} mb={6}>
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                        OK
                    </Button>
                </Box>
            </Box>
        )
    }, [handleClose])
    return (
        <Dialog disableScrollLock aria-labelledby="complete-project" open={modalCompleteProjectShow} maxWidth="md" fullWidth>
            <DialogTitle className={classes.closeButton} id="complete-project" onClose={handleClose} />
            <DialogContent className={classes.dialogContent}>{canSubmit ? outputContentSubmit : outputCannotSubmit}</DialogContent>
        </Dialog>
    )
})

export default ModalComplete
